import styles from './SocialAuthRow.module.css';

import React from 'react';
import { Button, Loader } from '@mantine/core';

/**
 * A single social / enterprise sign-in method rendered by <SocialAuthRow />.
 * `onClick` fires the method's existing auth action — this component only owns
 * presentation, never the auth logic.
 */
export type AuthMethod = {
  key: string;
  /** Full label, e.g. "Continue with Google". Shown when the segment is expanded. */
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
};

type Props = {
  methods: AuthMethod[];
  className?: string;
};

/**
 * Alternative sign-in methods under the "or" divider.
 *
 * - 0 methods  → renders nothing.
 * - 1 method   → a single full-width labeled button (identical to the previous
 *                UI, so Google-only login is visually unchanged).
 * - 2+ methods → on pointer/desktop, an expanding segmented control: each method
 *                shows only its icon at rest, sharing the row equally (total width
 *                = the Proceed button). Hovering or keyboard-focusing a segment
 *                slides it open to reveal its label while the others collapse
 *                (masked), never exceeding the row width. Adding a third method
 *                (e.g. Microsoft) needs no code change here — it just becomes a
 *                third segment. On touch / small screens (no hover) it falls back
 *                to stacked full-width labeled buttons.
 */
const SocialAuthRow = ({ methods, className }: Props) => {
  if (methods.length === 0) return null;

  const renderFullButton = (m: AuthMethod, extra = '') => (
    <Button
      key={m.key}
      variant="outline"
      className={`w-full border border-gray-300 text-gray-800 ${extra}`}
      leftSection={m.icon}
      onClick={m.onClick}
      loading={m.loading}
      disabled={m.disabled}>
      {m.label}
    </Button>
  );

  // Single method: keep the original full-width labeled button.
  if (methods.length === 1) {
    return (
      <div className={`w-full ${className ?? ''}`}>{renderFullButton(methods[0], 'mb-4')}</div>
    );
  }

  return (
    <div className={`w-full ${className ?? ''}`}>
      {/* Touch / small screens (no hover): stacked full-width labeled buttons. */}
      <div className="mb-4 flex flex-col gap-3 sm:hidden">
        {methods.map((m) => renderFullButton(m))}
      </div>

      {/*
        Pointer / desktop: expanding segmented control. At rest the segments
        share the row equally (icons only); hovering/focusing one expands it to
        fill the row while the others collapse to zero width (fully masked), and
        everything springs back on leave. See SocialAuthRow.module.css.
      */}
      <div className="mb-4 hidden sm:block">
        <div className={styles.row} role="group" aria-label="Other sign-in options">
          {methods.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={m.onClick}
              disabled={m.disabled}
              aria-label={m.label}
              title={m.label}
              className={styles.seg}>
              <span className={styles.icon}>{m.loading ? <Loader size={18} /> : m.icon}</span>
              <span className={styles.label}>{m.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SocialAuthRow;
